/**
 * Cliente HTTP del panel.
 *
 * Dos decisiones que valen para toda la app:
 *
 *  - `request()` NUNCA lanza. Devuelve siempre un `ApiResult` tipado, así que
 *    ninguna vista necesita envolver una llamada en try/catch: se pregunta por
 *    `result.successful` y se acabó.
 *  - La sesión va por cookie (`withCredentials`), y las mutaciones llevan el
 *    token CSRF que entrega `GET /api/session`. Ese token se guarda aquí
 *    porque el front es una SPA servida como archivos estáticos: no pasa por
 *    una vista de Rails donde el token pudiera venir en un `<meta>`.
 */
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import type { ApiErrorPayload, ApiResult, RawApiResponse } from '@/types/api'

export const apiClient: AxiosInstance = axios.create({
  baseURL: '/',
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

/** Lo llama el store de sesión con el token que devolvió el backend. */
export function setCsrfToken(token: string) {
  if (token) apiClient.defaults.headers.common['X-CSRF-Token'] = token
  else delete apiClient.defaults.headers.common['X-CSRF-Token']
}

/**
 * Quien quiera reaccionar a un 401 (el router, para mandar al login) se
 * suscribe aquí en vez de inspeccionar cada respuesta por su cuenta.
 */
type UnauthorizedHandler = () => void
let onUnauthorized: UnauthorizedHandler | null = null

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  onUnauthorized = handler
}

function normalize<T>(raw: RawApiResponse<T>): ApiResult<T> {
  if (raw?.successful) return { successful: true, data: raw.data as T }
  return {
    successful: false,
    error: raw?.error ?? { message: 'Respuesta sin información de error.' },
  }
}

function fromError(error: unknown): ApiResult<never> {
  if (!axios.isAxiosError(error)) {
    return { successful: false, error: { message: 'Error inesperado del cliente HTTP.' } }
  }

  const status = error.response?.status
  const payload = error.response?.data as RawApiResponse<unknown> | undefined

  if (status === 401) onUnauthorized?.()

  // El backend manda el sobre completo también en 4xx: sus mensajes ya están
  // en español y dicen más que cualquier texto genérico de aquí.
  if (payload?.error?.message) {
    const err: ApiErrorPayload = {
      message: payload.error.message,
      details: payload.error.details,
      code: payload.error.code,
    }
    return { successful: false, error: err }
  }

  if (status === 401) {
    return { successful: false, error: { message: 'Su sesión terminó. Vuelva a entrar.', code: 'UNAUTHORIZED' } }
  }
  if (status === 403) {
    return { successful: false, error: { message: 'No tiene permisos para esta acción.', code: 'FORBIDDEN' } }
  }
  if (status === 404) {
    return { successful: false, error: { message: 'No se encontró el registro.', code: 'NOT_FOUND' } }
  }
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
    return { successful: false, error: { message: 'Sin conexión con el servidor.', code: 'NETWORK_ERROR' } }
  }

  return { successful: false, error: { message: error.message || 'Error desconocido.', code: error.code } }
}

async function request<T>(config: AxiosRequestConfig): Promise<ApiResult<T>> {
  try {
    const response = await apiClient.request<RawApiResponse<T>>(config)
    return normalize<T>(response.data)
  } catch (error) {
    return fromError(error)
  }
}

export const api = {
  get<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return request<T>({ ...config, method: 'GET', url })
  },
  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return request<T>({ ...config, method: 'POST', url, data })
  },
  patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return request<T>({ ...config, method: 'PATCH', url, data })
  },
  delete<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return request<T>({ ...config, method: 'DELETE', url })
  },
}
